import { useCallback, useState } from 'react';
import type { GridApi } from 'ag-grid-community';

export type UseSelectableRowGridLogicParams<TData extends Record<string, unknown>> = {
  getRowUniqueId: (rowData: TData) => string | number,
  gridApi: GridApi | null,
}

export type UseSelectableRowGridLogicApi<TData extends Record<string, unknown>> = {
  selectedIds: Record<string, boolean>,
  getSelectedRows: () => TData[],
  selectedRowsCount: number,
  selectRow: (data: TData) => void,
  unselectRow: (data: TData) => void,
  selectFiltered: (additionalCondition?: (data: TData) => boolean) => void,
  unselectFiltered: (additionalCondition?: (data: TData) => boolean) => void,
  resetSelection: () => void,
  isSelected: (data: TData) => boolean,
}

export function useSelectableRowsGridLogic<TData extends Record<string, unknown>>({
  getRowUniqueId,
  gridApi,
}: UseSelectableRowGridLogicParams<TData>): UseSelectableRowGridLogicApi<TData> {
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});

  const isSelected = useCallback(
    (data: TData) => Boolean(selectedIds[getRowUniqueId(data)]),
    [selectedIds, getRowUniqueId]
  );

  const getSelectedRows = useCallback(() => {
    const selectedRows: TData[] = [];
    gridApi?.forEachNode(node => {
      if (isSelected(node.data)) {
        selectedRows.push(node.data);
      }
    });
    return selectedRows;
  }, [gridApi, isSelected]);

  const selectedRowsCount = Object.keys(selectedIds).length;

  const selectRow = useCallback((row: TData) => {
    setSelectedIds(selected => ({
      ...selected,
      [getRowUniqueId(row)]: true,
    }));
  }, [getRowUniqueId]);

  const unselectRow = useCallback((row: TData) => {
    setSelectedIds(({
      [getRowUniqueId(row)]: toRemove, // eslint-disable-line @typescript-eslint/no-unused-vars
      ...selected
    }) => selected);
  }, [getRowUniqueId]);

  const selectFiltered = useCallback((additionalCondition = () => true) => {
    const selectedRowsIds: Record<ReturnType<typeof getRowUniqueId>, boolean> = {};
    gridApi?.forEachNodeAfterFilter(node => {
      if (additionalCondition(node.data)) {
        selectedRowsIds[getRowUniqueId(node.data)] = true;
      }
    });
    setSelectedIds(selectedRowsIds);
  }, [getRowUniqueId, gridApi]);

  const unselectFiltered = useCallback((additionalCondition = () => true) => {
    setSelectedIds(({ ...selected }) => {
      gridApi?.forEachNodeAfterFilter(node => {
        if (additionalCondition(node.data)) {
          delete selected[getRowUniqueId(node.data)];
        }
      });
      return selected;
    });
  }, [getRowUniqueId, gridApi]);

  const resetSelection = () => {
    setSelectedIds({});
    gridApi?.refreshCells({
      force: true,
    });
  };

  return {
    selectedIds,
    getSelectedRows,
    selectedRowsCount,
    selectRow,
    unselectRow,
    selectFiltered,
    unselectFiltered,
    resetSelection,
    isSelected,
  };
}
